<template>
  <a-modal
    :visible="visible"
    :width="'calc(100vw - 32px)'"
    :style="{ maxWidth: '1180px', top: '24px', paddingBottom: '24px' }"
    :footer="null"
    :bodyStyle="{ padding: '0 8px 0 24px', overflow: 'hidden' }"
    :maskClosable="false"
    wrapClassName="bayley-report-modal"
    @cancel="cancel"
  >
    <template slot="title">
      <div class="modal-title">
        <span>贝利标准版评估报告</span>
        <span v-if="data" class="edition-tag">{{ editionName }}</span>
      </div>
    </template>

    <div v-if="data" class="report-scroll">
      <header class="report-overview">
        <div>
          <div class="report-kicker">评估报告</div>
          <h2>{{ displayValue(data.name) }}</h2>
          <div class="report-number">记分编号：{{ displayValue(data.scoring_num) }}</div>
        </div>
        <div class="report-meta">
          <div>
            <span>报告类型</span>
            <strong>{{ editionName }}</strong>
          </div>
          <div>
            <span>测试日期</span>
            <strong>{{ displayValue(data.test_date) }}</strong>
          </div>
        </div>
      </header>

      <section class="report-section">
        <div class="section-heading">
          <span class="section-mark">01</span>
          <div>
            <h3>基本信息</h3>
            <p>儿童、主试及照护者信息</p>
          </div>
        </div>
        <baseInfo :data="data" />
      </section>

      <section v-if="hasActionSection" class="report-section">
        <div class="section-heading">
          <span class="section-mark">02</span>
          <div>
            <h3>认知、语言与动作</h3>
            <p>核心能力维度原始分数</p>
          </div>
        </div>
        <div class="score-grid">
          <div v-for="item in actionScores" :key="item.field" class="score-card">
            <div class="score-label">
              <span>{{ item.label }}</span>
              <small>{{ item.code }}</small>
            </div>
            <div class="score-value">
              <strong>{{ displayValue(data[item.field]) }}</strong>
              <span>分</span>
            </div>
          </div>
        </div>
      </section>

      <section v-if="hasSocialSection" class="report-section">
        <div class="section-heading">
          <span class="section-mark">{{ hasActionSection ? '03' : '02' }}</span>
          <div>
            <h3>社会-情绪与适应行为</h3>
            <p>社会情绪及日常适应能力分数</p>
          </div>
        </div>
        <div class="score-grid score-grid-social">
          <div v-for="item in socialScores" :key="item.field" class="score-card">
            <div class="score-label">
              <span>{{ item.label }}</span>
              <small>{{ item.code }}</small>
            </div>
            <div class="score-value">
              <strong>{{ displayValue(data[item.field]) }}</strong>
              <span>分</span>
            </div>
          </div>
        </div>
      </section>

      <section class="report-section report-parameters">
        <div class="section-heading compact-heading">
          <span class="section-mark">{{ parameterSectionNumber }}</span>
          <div>
            <h3>评估参数</h3>
            <p>报告计算与解释参数</p>
          </div>
        </div>
        <div class="parameter-grid">
          <div class="parameter-item">
            <span>显著性水平</span>
            <strong>{{ displayValue(data.significance_level) }}</strong>
          </div>
          <div class="parameter-item">
            <span>置信区间</span>
            <strong>{{ confidenceProbability }}</strong>
          </div>
          <div v-if="hasSocialSection" class="parameter-item parameter-item-wide">
            <span>最高掌握阶段</span>
            <strong>{{ highestStageText }}</strong>
          </div>
        </div>
      </section>

      <section v-if="hasActionSection" class="report-section">
        <div class="section-heading">
          <span class="section-mark">{{ observationSectionNumber }}</span>
          <div>
            <h3>行为观察清单</h3>
            <p>主试与照护者观察结果对照</p>
          </div>
        </div>
        <div class="observation-table">
          <div class="observation-row observation-head">
            <span>观察项目</span>
            <span>主试</span>
            <span>照护者</span>
          </div>
          <div v-for="(label, index) in observationLabels" :key="label" class="observation-row">
            <strong>{{ label }}</strong>
            <span>{{ observationText(data[`observation_ex${index + 1}`]) }}</span>
            <span>{{ observationText(data[`observation_tk${index + 1}`]) }}</span>
          </div>
        </div>
      </section>

      <section v-if="hasSocialSection" class="report-section">
        <div class="section-heading">
          <span class="section-mark">{{ socialDetailSectionNumber }}</span>
          <div>
            <h3>补充评估指标</h3>
            <p>社会-情绪及适应行为施测情况</p>
          </div>
        </div>
        <div class="supplement-summary">
          <div>
            <span>第1-8题原始总分</span>
            <strong>{{ displayValue(data.score_original) }}</strong>
          </div>
          <div>
            <span>社会-情绪无法判断/猜测题数</span>
            <strong>{{ displayValue(data.guess_se) }}</strong>
          </div>
        </div>
        <div class="adaptive-table">
          <div class="adaptive-row adaptive-head">
            <span>适应行为领域</span>
            <span>猜测题数</span>
            <span>施测题数</span>
          </div>
          <div v-for="item in adaptiveStats" :key="item.guessField" class="adaptive-row">
            <strong>{{ item.label }}（{{ item.code }}）</strong>
            <span>{{ displayValue(data[item.guessField]) }}</span>
            <span>{{ displayValue(data[item.totalField]) }}</span>
          </div>
        </div>
      </section>

      <section v-if="hasDetailScores" class="report-section detail-score-section">
        <div class="section-heading">
          <span class="section-mark">{{ detailScoreSectionNumber }}</span>
          <div>
            <h3>详细题目分数</h3>
            <p>按评估维度查看每道题目的原始评分</p>
          </div>
        </div>
        <bayley-score-detail
          class="detail-score-view"
          :value="detailScores"
          :edition="data.edition"
          readonly
        />
      </section>
    </div>
  </a-modal>
</template>

<script>
import { Detail } from '../../commonApi';
import BayleyScoreDetail from '../bayley-score-detail';

const ACTION_SCORES = [
  { field: 'score_cg', label: '认知', code: 'CG' },
  { field: 'score_rc', label: '接受性沟通', code: 'RC' },
  { field: 'score_ec', label: '表达性沟通', code: 'EC' },
  { field: 'score_fm', label: '精细动作', code: 'FM' },
  { field: 'score_gm', label: '粗大动作', code: 'GM' },
];

const SOCIAL_SCORES = [
  { field: 'score_se', label: '社会-情绪', code: 'SE' },
  { field: 'score_rec', label: '适应行为-接受性', code: 'REC' },
  { field: 'score_exp', label: '适应行为-表达性', code: 'EXP' },
  { field: 'score_per', label: '个人自理', code: 'PER' },
  { field: 'score_ipr', label: '人际关系', code: 'IPR' },
  { field: 'score_pla', label: '玩耍与休闲', code: 'PLA' },
];

const OBSERVATION_LABELS = [
  '积极影响', '热情', '探索', '容易投入', '合作性', '适度活跃', '对改变的适应性',
  '警觉性', '注意力分散', '动作张力', '触觉防御', '恐惧/焦虑', '消极影响',
];

const ADAPTIVE_STATS = [
  { label: '接受性', code: 'REC', guessField: 'guess_rec', totalField: 'guess_rec_total' },
  { label: '表达性', code: 'EXP', guessField: 'guess_exp', totalField: 'guess_exp_total' },
  { label: '个人自理', code: 'PER', guessField: 'guess_per', totalField: 'guess_per_total' },
  { label: '人际关系', code: 'IPR', guessField: 'guess_ipr', totalField: 'guess_ipr_total' },
  { label: '玩耍与休闲', code: 'PLA', guessField: 'guess_pla', totalField: 'guess_pla_total' },
];

const HIGHEST_STAGE_TEXT = {
  1: '还未掌握第一阶段',
  2: '第1阶段',
  3: '第2阶段',
  4: '第3阶段',
  5: '第4(a)阶段',
  6: '第4(b)阶段',
  7: '第5(a)阶段',
  8: '第5(b)阶段',
  9: '第6阶段',
};

export default {
  components: { BayleyScoreDetail },
  props: {
    item: Object,
    detailVisible: Boolean,
  },
  data() {
    return {
      visible: false,
      id: '',
      data: null,
      actionScores: ACTION_SCORES,
      socialScores: SOCIAL_SCORES,
      observationLabels: OBSERVATION_LABELS,
      adaptiveStats: ADAPTIVE_STATS,
    };
  },
  computed: {
    editionName() {
      if (!this.data) return '';
      if (String(this.data.edition) === '1') return '社会-情绪与适应行为';
      if (String(this.data.edition) === '2') return '认知、语言与动作';
      return '综合报告';
    },
    hasActionSection() {
      return this.data && ['2', '3'].includes(String(this.data.edition));
    },
    hasSocialSection() {
      return this.data && ['1', '3'].includes(String(this.data.edition));
    },
    parameterSectionNumber() {
      return this.hasActionSection && this.hasSocialSection ? '04' : '03';
    },
    observationSectionNumber() {
      return this.hasSocialSection ? '05' : '04';
    },
    socialDetailSectionNumber() {
      return this.hasActionSection ? '06' : '04';
    },
    detailScoreSectionNumber() {
      if (this.hasActionSection && this.hasSocialSection) return '07';
      return this.hasActionSection || this.hasSocialSection ? '05' : '04';
    },
    confidenceProbability() {
      const value = this.data && this.data.confidence_probability;
      return value === null || value === undefined || value === '' ? '—' : `${value}%`;
    },
    highestStageText() {
      return HIGHEST_STAGE_TEXT[String(this.data && this.data.highest_stage)] || '—';
    },
    detailScores() {
      const scores = this.data && this.data.detail_scores;
      if (scores && typeof scores === 'object') return scores;
      if (typeof scores === 'string' && scores) {
        try {
          return JSON.parse(scores);
        } catch (e) {
          return {};
        }
      }
      return {};
    },
    hasDetailScores() {
      return this.data && String(this.data.detail_score_enabled) === '1'
        && Object.keys(this.detailScores).length > 0;
    },
  },
  watch: {
    item: {
      handler(value) {
        if (!value) return;
        this.data = value;
        this.id = value.id;
      },
      immediate: true,
      deep: true,
    },
    detailVisible: {
      handler(value) {
        this.visible = value;
        if (value) this.getDetail();
      },
      immediate: true,
    },
  },
  methods: {
    async getDetail() {
      if (!this.id) return;
      const res = await Detail({ id: this.id });
      if (res && res.code == 0) this.data = res.bayleyAssessment;
    },
    displayValue(value) {
      return value === null || value === undefined || value === '' ? '—' : value;
    },
    observationText(value) {
      const values = {
        1: '非常典型；总是如此',
        2: '有点典型；有时如此',
        3: '完全不典型；从不或很少如此',
      };
      return values[String(value)] || '—';
    },
    cancel() {
      this.visible = false;
      this.$emit('detailCancel');
    },
  },
};
</script>

<style scoped>
.modal-title {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
  color: #27232a;
  font-size: 17px;
  font-weight: 600;
}

.edition-tag {
  max-width: 320px;
  overflow: hidden;
  padding: 3px 9px;
  border: 1px solid #ded2dc;
  border-radius: 4px;
  color: #6f5269;
  background: #faf7f9;
  font-size: 12px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.report-scroll {
  max-height: calc(100vh - 118px);
  overflow-x: hidden;
  overflow-y: auto;
  padding: 24px 16px 36px 0;
  color: #363239;
}

.report-scroll::-webkit-scrollbar {
  width: 6px;
}

.report-scroll::-webkit-scrollbar-thumb {
  border-radius: 3px;
  background: #cfc8cd;
}

.report-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.report-overview {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 32px;
  padding: 6px 4px 28px;
  border-bottom: 2px solid #3e7b70;
}

.report-kicker {
  margin-bottom: 7px;
  color: #3e7b70;
  font-size: 12px;
  font-weight: 600;
}

.report-overview h2 {
  margin: 0;
  color: #242126;
  font-size: 28px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 1.35;
}

.report-number {
  margin-top: 8px;
  color: #777178;
  font-size: 13px;
}

.report-meta {
  display: grid;
  min-width: 360px;
  grid-template-columns: repeat(2, minmax(150px, 1fr));
  gap: 24px;
}

.report-meta div {
  padding-left: 14px;
  border-left: 2px solid #ded7dc;
}

.report-meta span,
.parameter-item span,
.supplement-summary span {
  display: block;
  margin-bottom: 5px;
  color: #858086;
  font-size: 12px;
}

.report-meta strong {
  color: #373239;
  font-size: 14px;
  font-weight: 600;
}

.report-section {
  padding: 30px 4px 34px;
  border-bottom: 1px solid #ece8eb;
}

.report-section:last-child {
  border-bottom: 0;
}

.section-heading {
  display: flex;
  align-items: center;
  gap: 13px;
  margin-bottom: 22px;
}

.section-mark {
  display: grid;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  place-items: center;
  border: 1px solid #d8cfd6;
  border-radius: 4px;
  color: #75536c;
  background: #faf8fa;
  font-size: 12px;
  font-weight: 600;
}

.section-heading h3 {
  margin: 0;
  color: #2f2a30;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0;
}

.section-heading p {
  margin: 3px 0 0;
  color: #918b91;
  font-size: 12px;
}

.score-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}

.score-grid-social {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.score-card {
  min-width: 0;
  padding: 16px;
  border: 1px solid #e5e0e3;
  border-radius: 6px;
  background: #fff;
}

.score-label {
  display: flex;
  min-height: 38px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  color: #585259;
  font-size: 13px;
}

.score-label small {
  flex: 0 0 auto;
  color: #3e7b70;
  font-size: 11px;
  font-weight: 600;
}

.score-value {
  display: flex;
  align-items: baseline;
  gap: 5px;
  margin-top: 10px;
}

.score-value strong {
  color: #6f315f;
  font-size: 28px;
  font-weight: 600;
}

.score-value span {
  color: #9a9499;
  font-size: 12px;
}

.parameter-grid,
.supplement-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1px;
  overflow: hidden;
  border: 1px solid #e6e1e4;
  border-radius: 6px;
  background: #e6e1e4;
}

.parameter-item,
.supplement-summary > div {
  min-width: 0;
  padding: 16px 18px;
  background: #faf9fa;
}

.parameter-item strong,
.supplement-summary strong {
  color: #383238;
  font-size: 16px;
  font-weight: 600;
}

.observation-table,
.adaptive-table {
  overflow: hidden;
  border: 1px solid #e5e0e3;
  border-radius: 6px;
}

.observation-row,
.adaptive-row {
  display: grid;
  min-height: 48px;
  align-items: center;
  border-bottom: 1px solid #efebed;
}

.observation-row {
  grid-template-columns: 180px repeat(2, minmax(0, 1fr));
}

.adaptive-row {
  grid-template-columns: minmax(240px, 1.4fr) repeat(2, minmax(120px, 1fr));
}

.observation-row:last-child,
.adaptive-row:last-child {
  border-bottom: 0;
}

.observation-row > *,
.adaptive-row > * {
  min-width: 0;
  padding: 10px 16px;
  overflow-wrap: anywhere;
}

.observation-row > * + *,
.adaptive-row > * + * {
  border-left: 1px solid #efebed;
}

.observation-row strong,
.adaptive-row strong {
  color: #4f494f;
  font-size: 13px;
  font-weight: 600;
}

.observation-row span,
.adaptive-row span {
  color: #6f696f;
  font-size: 13px;
  line-height: 1.55;
}

.observation-head,
.adaptive-head {
  min-height: 42px;
  background: #f5f2f4;
}

.observation-head span,
.adaptive-head span {
  color: #5f585e;
  font-weight: 600;
}

.supplement-summary {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 18px;
}

.detail-score-section {
  padding-bottom: 0;
}

.detail-score-view {
  margin: 0;
}

@media (max-width: 960px) {
  .score-grid,
  .score-grid-social {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .report-overview {
    align-items: flex-start;
    flex-direction: column;
  }

  .report-meta {
    width: 100%;
  }
}

@media (max-width: 700px) {
  .report-scroll {
    padding-top: 18px;
  }

  .report-overview h2 {
    font-size: 22px;
  }

  .report-meta,
  .score-grid,
  .score-grid-social,
  .parameter-grid,
  .supplement-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .observation-row {
    grid-template-columns: 112px repeat(2, minmax(160px, 1fr));
  }

  .observation-table,
  .adaptive-table {
    overflow-x: auto;
  }

  .adaptive-row {
    min-width: 580px;
  }
}

@media (max-width: 520px) {
  .modal-title {
    padding-right: 24px;
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
  }

  .edition-tag {
    max-width: 100%;
  }

  .report-meta,
  .score-grid,
  .score-grid-social,
  .parameter-grid,
  .supplement-summary {
    grid-template-columns: 1fr;
  }

  .report-section {
    padding-top: 24px;
  }

  .section-heading h3 {
    font-size: 16px;
  }

  .observation-row {
    min-width: 580px;
  }
}
</style>

<style>
.bayley-report-modal .ant-modal {
  top: 24px;
  max-width: 1180px;
  padding-bottom: 24px;
}

@media (max-width: 520px) {
  .bayley-report-modal .ant-modal {
    top: 16px;
    padding-bottom: 16px;
  }
}
</style>
